/**
 * CategorySettingsPage
 * 경비 분류 설정 페이지
 */

import React, { useState, useEffect, useCallback } from 'react';
import { TaxRepository } from '../../repositories/TaxRepository';
import { TaxCategory, CreateCategoryRequest, UpdateCategoryRequest } from '../../models/Tax';

const taxRepository = new TaxRepository();

export const CategorySettingsPage: React.FC = () => {
  const [categories, setCategories] = useState<TaxCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // 폼 상태
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    keywords: '',
    sort_order: 0,
    is_active: true,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await taxRepository.getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '카테고리 로드 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setForm({ code: '', name: '', description: '', keywords: '', sort_order: 0, is_active: true });
    setEditId(null);
    setShowAdd(false);
  };

  const startEdit = (cat: TaxCategory) => {
    setEditId(cat.id);
    setShowAdd(false);
    setForm({
      code: cat.code,
      name: cat.name,
      description: cat.description || '',
      keywords: (cat.keywords || []).join(', '),
      sort_order: cat.sort_order,
      is_active: cat.is_active,
    });
  };

  const startAdd = () => {
    resetForm();
    setShowAdd(true);
  };

  const handleSave = async () => {
    const keywordsArray = form.keywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    if (editId) {
      // 수정
      try {
        await taxRepository.updateCategory(editId, {
          name: form.name,
          description: form.description || undefined,
          keywords: keywordsArray,
          sort_order: form.sort_order,
          is_active: form.is_active,
        });
        resetForm();
        loadData();
      } catch (err) {
        setError(err instanceof Error ? err.message : '수정 실패');
      }
    } else {
      // 추가
      if (!form.code || !form.name) {
        setError('코드와 이름은 필수입니다.');
        return;
      }
      try {
        await taxRepository.createCategory({
          code: form.code,
          name: form.name,
          description: form.description || undefined,
          keywords: keywordsArray,
          sort_order: form.sort_order,
        });
        resetForm();
        loadData();
      } catch (err) {
        setError(err instanceof Error ? err.message : '추가 실패');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">경비 분류 설정</h1>
        <button
          onClick={startAdd}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          + 분류 추가
        </button>
      </div>

      <div className="bg-blue-50 rounded-md p-3">
        <p className="text-xs text-blue-700">
          키워드는 CSV 업로드 시 사용처 이름에서 자동으로 분류를 매칭하는 데 사용됩니다.
          쉼표(,)로 구분하여 여러 개 입력할 수 있습니다.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-xs text-red-400 mt-1 underline">닫기</button>
        </div>
      )}

      {/* 추가 폼 */}
      {showAdd && (
        <div className="bg-white rounded-lg shadow p-4 border-2 border-blue-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">새 분류 추가</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">코드 *</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="예: equipment"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">이름 *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="예: 장비/기계"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">설명</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="예: 업무용 장비 구매"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">키워드 (쉼표 구분)</label>
              <input
                type="text"
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="예: 컴퓨터, 노트북, 모니터"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">정렬 순서</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
              추가
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              취소
            </button>
          </div>
        </div>
      )}

      {/* 카테고리 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">순서</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">코드</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">이름</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">설명</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">키워드</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">상태</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">작업</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">불러오는 중...</td></tr>
            ) : categories.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">카테고리가 없습니다.</td></tr>
            ) : (
              categories.map((cat) => (
                <tr key={cat.id} className={`hover:bg-gray-50 ${editId === cat.id ? 'bg-blue-50' : ''}`}>
                  {editId === cat.id ? (
                    <>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={form.sort_order}
                          onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                          className="w-14 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">{cat.code}</td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={form.description}
                          onChange={(e) => setForm({ ...form, description: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={form.keywords}
                          onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="쉼표 구분"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={form.is_active}
                          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button onClick={handleSave} className="text-xs text-blue-600 hover:text-blue-800 mr-2">저장</button>
                        <button onClick={resetForm} className="text-xs text-gray-600 hover:text-gray-800">취소</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-500">{cat.sort_order}</td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">{cat.code}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{cat.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{cat.description || '-'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {(cat.keywords || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {(cat.keywords || []).slice(0, 5).map((kw, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">{kw}</span>
                            ))}
                            {(cat.keywords || []).length > 5 && (
                              <span className="text-gray-400">+{(cat.keywords || []).length - 5}</span>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${cat.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {cat.is_active ? '활성' : '비활성'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => startEdit(cat)} className="text-xs text-blue-600 hover:text-blue-800">수정</button>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
